"use client"

import { useState, useEffect, useCallback } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Calendar, Check, ArrowRight, LayoutGrid, ChevronLeft, ChevronRight } from "lucide-react"
import { DEMO_WHATSAPP_URL } from "@/lib/demo-whatsapp"

const modulos = [
  {
    id: "panel",
    num: "Módulo 01 — Panel",
    nombre: "Panel",
    valor: "Tu clínica de un vistazo",
    resultado: "Entras y sabes cuántas citas tienes, cuánto ingresaste y qué necesita atención hoy.",
    puntos: ["KPIs del día", "Caja en tiempo real", "Alertas de inventario", "Agenda integrada", "Tablero de tareas"],
  },
  {
    id: "pacientes",
    num: "Módulo 02 — Pacientes",
    nombre: "Pacientes",
    valor: "Expediente clínico completo",
    resultado: "Antecedentes, consultas, documentos y tratamientos en un perfil por paciente.",
    puntos: ["Registro multi-paso guiado", "Búsqueda y exportación", "Consulta, pago y notas desde la lista", "Cotizaciones y recetas"],
  },
  {
    id: "agenda",
    num: "Módulo 03 — Agenda",
    nombre: "Agenda",
    valor: "Agenda Inteligente",
    resultado: "Organiza las citas de tu clínica en un calendario visual y fácil de usar. Planifica por día, semana o mes, evita choques de horario y mantén a tu equipo y pacientes siempre alineados.",
    puntos: ["Calendario visual en tiempo real", "Vistas día, semana y mes", "Agendamiento sin conflictos", "Recordatorios por WhatsApp", "Visualización de citas por dentista", "Marca vacaciones o horarios no disponibles"],
  },
  {
    id: "odontograma",
    num: "Módulo 04 — Odontograma",
    nombre: "Odontograma",
    valor: "Odontograma Digital Interactivo",
    resultado: "Documenta el estado bucal de cada paciente con un odontograma gráfico, preciso y fácil de usar. Registra diagnósticos por diente y superficie, vincula procedimientos y genera cotizaciones sin salir del mapa dental.",
    puntos: ["Dentición adulta e infantil", "Edición por pieza dental", "Cotizador de tratamientos", "Historial y exportación de datos", "Diagnósticos vinculados a tratamientos"],
  },
  {
    id: "pagos",
    num: "Módulo 05 — Pagos",
    nombre: "Pagos",
    valor: "Cobros sin lagunas",
    resultado: "Controla los ingresos de tu clínica en un solo lugar. Registra cobros, administra planes a plazos, da seguimiento a pendientes y genera recibos profesionales sin complicaciones.",
    puntos: ["Registro rápido de pagos", "Planes a cuotas", "Pagos pendientes", "Recibos en PDF", "Control de salidas de caja"],
  },
  {
    id: "inventario",
    num: "Módulo 06 — Inventario",
    nombre: "Inventario",
    valor: "Stock siempre visible",
    resultado: "Controla insumos, materiales y existencias de tu clínica sin hojas de cálculo. Recibe alertas antes de quedarte sin stock o de que algo caduque, y lleva trazabilidad completa de cada movimiento.",
    puntos: ["Catálogo de productos", "Control por lotes", "Alertas automáticas", "Entradas y salidas", "Historial de movimientos"],
  },
  {
    id: "laboratorio",
    num: "Módulo 07 — Laboratorio",
    nombre: "Laboratorio",
    valor: "Órdenes que no se pierden",
    resultado: "Gestiona trabajos protésicos y laboratorios externos sin perder el control. Registra cada orden, da seguimiento visual por etapas y mantén vinculados paciente, laboratorio, fechas y costos en un solo lugar.",
    puntos: ["Estados visuales por orden", "Directorio de laboratorios", "Seguimiento por etapas", "Control de costos"],
  },
  {
    id: "reportes",
    num: "Módulo 08 — Reportes",
    nombre: "Reportes",
    valor: "Decisiones con datos",
    resultado: "Convierte la operación de tu clínica en números claros. Analiza pacientes, consultas, ingresos y egresos por periodo, visualiza tendencias y exporta reportes listos para compartir.",
    puntos: ["KPIs operativos y financieros", "Filtros por fechas", "Exportación de datos"],
  },
  {
    id: "clinicas",
    num: "Módulo 09 — Multi-clínica",
    nombre: "Multi-clínica",
    valor: "Varias sedes, un control",
    resultado: "Administra sucursales, usuarios y dentistas con permisos por rol desde una cuenta.",
    puntos: ["Gestión centralizada", "Acceso por rol", "Vista por sede"],
  },
]

const stats = [
  { n: "9", l: "módulos integrados nativamente" },
  { n: "1", l: "login para toda tu operación" },
  { n: "0", l: "integraciones externas necesarias" },
]

const carouselImages = [
  "/features/f1.svg",
  "/features/f2.svg",
  "/features/f3.svg",
  "/features/f4.svg",
  "/features/f5.svg",
  "/features/f6.svg",
  // "/features/f8.svg"
]

export function FeatureHub() {
  const [active, setActive] = useState(0)
  const [currentImageIdx, setCurrentImageIdx] = useState(0)
  const current = modulos[active]

  const nextImage = useCallback(() => {
    setCurrentImageIdx((prev) => (prev + 1) % carouselImages.length)
  }, [])

  const prevImage = useCallback(() => {
    setCurrentImageIdx((prev) => (prev === 0 ? carouselImages.length - 1 : prev - 1))
  }, [])

  useEffect(() => {
    const timer = setInterval(nextImage, 4000)
    return () => clearInterval(timer)
  }, [nextImage])

  return (
    <section id="funcionalidades" className="py-16 sm:py-20 lg:py-32 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* --- SECCIÓN SUPERIOR: Encabezado --- */}
        <div className="max-w-3xl mx-auto flex flex-col items-center text-center mb-12 sm:mb-16">
          <Badge
            className="mb-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider"
            style={{ backgroundColor: "#e0f4f5", color: "#00676E", border: "none" }}
          >
            <LayoutGrid className="h-3 w-3" />
            Plataforma completa
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 sm:mb-6 leading-tight text-balance">
            9 módulos. Una sola plataforma.
            <br />
            Cero parches.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Todo lo que ves aquí existe en el producto real. Explora cada módulo y compruébalo.
          </p>
        </div>

        {/* --- SECCIÓN CENTRAL: Grid (Izquierda Imágenes, Derecha Tarjeta) --- */}
        {/* CAMBIO AQUÍ: Cambié items-start por items-center para que se alineen verticalmente al medio */}
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-center">
          
          {/* Lado Izquierdo: Carrusel de Imágenes Flotantes */}
          {/* CAMBIO AQUÍ: Quité 'sticky top-28' y conservé 'aspect-video' */}
          <div className="relative w-full aspect-video flex items-center justify-center group p-4"> {/* Añadí un padding */}            
            {/* Imágenes con object-contain y drop-shadow para el efecto flotante */}
            {carouselImages.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt={`Vista de la aplicación ${idx + 1}`}
                // AÑADÍ 'scale-110' Y AUMENTÉ EL 'object-contain'
                className={`absolute inset-0 w-full h-full object-contain scale-130 drop-shadow-2xl transition-all duration-700 ease-in-out ${
                  idx === currentImageIdx ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              />
            ))}

            {/* Controles manuales (Aparecen al pasar el mouse) */}
            <button
              onClick={prevImage}
              className="absolute left-0 sm:-left-6 z-20 p-2 sm:p-3 text-muted-foreground hover:text-foreground bg-white/90 hover:bg-white rounded-full shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300"
              aria-label="Imagen anterior"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <button
              onClick={nextImage}
              className="absolute right-0 sm:-right-6 z-20 p-2 sm:p-3 text-muted-foreground hover:text-foreground bg-white/90 hover:bg-white rounded-full shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300"
              aria-label="Siguiente imagen"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            
            {/* Indicadores de progreso (Dots) */}
            <div className="absolute -bottom-8 left-0 right-0 z-20 flex justify-center gap-2">
              {carouselImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIdx(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    idx === currentImageIdx 
                      ? "w-6 bg-primary/90" 
                      : "w-2 bg-foreground/20 hover:bg-foreground/40"
                  }`}
                  aria-label={`Ir a la imagen ${idx + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Lado Derecho: Tarjeta de Especificaciones */}
          <div className="flex flex-col w-full lg:min-h-[520px] rounded-xl border border-border/40 bg-white shadow-sm p-5 sm:p-6 lg:p-8 mt-8 lg:mt-0">
            <div className="mb-6 shrink-0">
              <label htmlFor="modulo-select" className="sr-only">
                Seleccionar módulo
              </label>
              <Select
                value={String(active)}
                onValueChange={(value) => setActive(Number(value))}
              >
                <SelectTrigger id="modulo-select" className="w-full h-11 text-sm font-medium [&>span]:line-clamp-1 [&>span]:text-left">
                  <SelectValue placeholder="Elige un módulo" />
                </SelectTrigger>
                <SelectContent>
                  {modulos.map((m, i) => (
                    <SelectItem key={m.id} value={String(i)}>
                      {m.num}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col flex-1 min-h-0">
              <p
                className="text-[11px] font-semibold uppercase tracking-widest mb-3 shrink-0"
                style={{ color: "#00676E" }}
              >
                {current.num}
              </p>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground mb-2 leading-snug shrink-0">
                {current.valor}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5 shrink-0">
                {current.resultado}
              </p>
              <ul className="flex flex-col gap-2.5 mb-6 flex-1">
                {current.puntos.map((punto) => (
                  <li key={punto} className="flex items-start gap-2 text-sm text-foreground">
                    <span
                      className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: "#00676E15" }}
                    >
                      <Check className="h-2.5 w-2.5" style={{ color: "#00676E" }} />
                    </span>
                    {punto}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 pt-4 mt-auto border-t border-border/40 shrink-0">
                <Button
                  asChild
                  className="w-full sm:w-auto text-white text-sm"
                  style={{ backgroundColor: "#F97316" }}
                >
                  <a href={DEMO_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <Calendar className="mr-2 h-4 w-4" />
                    Ver demo en vivo
                  </a>
                </Button>
                <button
                  onClick={() => setActive((active + 1) % modulos.length)}
                  className="flex items-center justify-center sm:justify-start gap-1 text-sm font-medium hover:underline py-1"
                  style={{ color: "#00676E" }}
                >
                  Siguiente módulo
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* --- SECCIÓN INFERIOR: Métricas Centradas --- */}
        <div className="max-w-4xl mx-auto mt-5 sm:mt-10 pt-8 sm:pt-12 border-t border-border/40">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16 lg:gap-24 text-center">
            {stats.map((stat) => (
              <div key={stat.l} className="flex flex-col items-center">
                <p className="text-2xl sm:text-5xl font-extrabold tracking-tight" style={{ color: "#00676E" }}>
                  {stat.n}
                </p>
                <p className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                  {stat.l}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  )
}